import React from "react";
import { useAppSelector } from "../../../store";
import { groupSummaries } from "../../../store/profiling/selectors";
import { groupEventWindows, eventOptionKey, type EventOption } from "../../../store/profiling/filtering";
import type { CycleEntry } from "../../../store/profiling/types";
import { tracesInWindow } from "../../../store/tracing/selectors";
import type { Trace } from "../../../store/tracing/types";
import { formatDate } from "../../../shared/utilities";
import { traceKindLabel, traceSourceLabel } from "../traceFormat";
import clsx from "clsx";

/** Compact one-line rendering of a trace value (old/new/error). */
function formatValue(v: unknown): string {
    if (v === undefined) return "—";
    try {
        const s = typeof v === "string" ? v : JSON.stringify(v);
        if (s == null) return String(v);
        return s.length > 80 ? `${s.slice(0, 80)}…` : s;
    } catch {
        return String(v);
    }
}

function TriggerRow({ trace, active, onToggle }: { trace: Trace; active: boolean; onToggle?: () => void }) {
    const { event } = trace;
    const isError = event.kind === "DatasourceErrorOccurred";
    return (
        <div
            className={clsx("trigger-row", isError && "error", active && "active", onToggle && "clickable")}
            onClick={onToggle}
            title={onToggle ? "Toggle this event in the filter" : undefined}
        >
            <span className="trigger-time">{formatDate(trace.timestamp, "HH:mm:ss.SSS")}</span>
            <span className="trigger-kind">{traceKindLabel(event.kind)}</span>
            {event["name"] ? <span className="trigger-name">{event["name"]}</span> : null}
            <span className="trigger-detail">
                {isError
                    ? formatValue((event as any).error)
                    : <>{formatValue(event["oldValue"])} <span className="arrow">→</span> {formatValue(event["newValue"])}</>}
            </span>
            <span className="trigger-source">{traceSourceLabel(event.source)}</span>
        </div>
    );
}

type Props = {
    selectedCycle: CycleEntry | undefined;
    /** Toggles the row's event (kind + name) in the shared event filter (rows are clickable). */
    onToggleEvent?: (event: EventOption) => void;
    /** Keys (`eventOptionKey`) of the events currently in the filter — their rows get an accent. */
    activeEventKeys?: Set<string>;
};

/**
 * The recorded events that likely *caused* the selected render pass: traces whose `perfTime` falls in the
 * window between the previous pass's start and this pass's start (`(T_prev, T]`).  Requires tracing to be
 * on — which it is whenever profiling is on (the toggles are coupled).
 */
export function TriggeredBy({ selectedCycle, onToggleEvent, activeEventKeys }: Props) {
    const profiling = useAppSelector(s => s.profiling);
    const traces = useAppSelector(s => s.tracing.traces);

    const causing = React.useMemo(() => {
        if (!selectedCycle) return [];
        const window = groupEventWindows(groupSummaries(profiling), profiling.startedAtPerf ?? -Infinity).get(selectedCycle.groupId);
        return window ? tracesInWindow(traces, window.fromPerf, window.toPerf) : [];
    }, [profiling, traces, selectedCycle]);

    if (!selectedCycle) return null;

    return (
        <div className="triggered-by">
            <div className="triggered-by-title">Triggered by <span className="count">{causing.length}</span></div>
            {causing.length === 0 ? (
                <div className="triggered-by-empty">No recorded events precede this pass (enable tracing / interact with the canvas).</div>
            ) : (
                <div className="trigger-list">
                    {causing.map((t, i) => {
                        const option: EventOption = { kind: t.event.kind, name: t.event.name };
                        return (
                            <TriggerRow
                                key={i}
                                trace={t}
                                active={activeEventKeys?.has(eventOptionKey(option)) ?? false}
                                onToggle={onToggleEvent ? () => onToggleEvent(option) : undefined}
                            />
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default React.memo(TriggeredBy);
